import { useEffect, useMemo, useRef } from "react";
import { Box } from "@mui/material";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
} from "chart.js";
import { colors } from "../ui/colors";
import { useNutritionDisplayMode } from "../../contexts/NutritionDisplayContext";
import { POINTS, energyFigure, energyLabel } from "../nutrition/v2/ui/nutritionUnits";

ChartJS.register(ArcElement, Tooltip);

const DEFAULT_SLICE_COLORS = [colors.primary, "#69B8AF", "#B9D9D5"];
const LABELS = ["פחמימה", "חלבון", "שומן"];
const TOOLTIP_ID = "nutrition-donut-tooltip";

const centerTextPlugin = {
  id: "centerText",
  afterDraw(chart, _args, opts) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    const { title, subtitle } = opts || {};
    if (!title) return;
    const cx = (chartArea.left + chartArea.right) / 2;
    const cy = (chartArea.top + chartArea.bottom) / 2;
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = colors.textPrimary;
    ctx.font = "700 20px Heebo, system-ui, sans-serif";
    ctx.fillText(title, cx, cy - (subtitle ? 8 : 0));
    if (subtitle) {
      ctx.fillStyle = colors.textSecondary;
      ctx.font = "500 11px Heebo, system-ui, sans-serif";
      ctx.fillText(subtitle, cx, cy + 12);
    }
    ctx.restore();
  },
};

const getOrCreateTooltipEl = () => {
  let el = document.getElementById(TOOLTIP_ID);
  if (el) return el;
  el = document.createElement("div");
  el.id = TOOLTIP_ID;
  el.dir = "rtl";
  Object.assign(el.style, {
    position: "absolute",
    pointerEvents: "none",
    background: "#fff",
    border: "1px solid rgba(15,20,40,.1)",
    borderRadius: "8px",
    padding: "8px 10px",
    boxShadow: "0 4px 14px rgba(15,20,40,.1)",
    fontSize: "12px",
    fontFamily: "Heebo, system-ui, sans-serif",
    color: colors.textPrimary,
    opacity: 0,
    transition: "opacity .12s ease",
    zIndex: 1500,
    whiteSpace: "nowrap",
  });
  document.body.appendChild(el);
  return el;
};

const externalTooltip = (context) => {
  const { chart, tooltip } = context;
  const el = getOrCreateTooltipEl();

  if (tooltip.opacity === 0) {
    el.style.opacity = 0;
    return;
  }

  if (tooltip.body) {
    const idx = tooltip.dataPoints?.[0]?.dataIndex ?? 0;
    const label = tooltip.dataPoints?.[0]?.label || "";
    const value = tooltip.dataPoints?.[0]?.parsed ?? 0;
    const total = (chart.data.datasets[0].data || []).reduce((a, b) => a + (b || 0), 0);
    const pct = total > 0 ? Math.round((value / total) * 1000) / 10 : 0;
    const palette = chart.data.datasets[0].backgroundColor || DEFAULT_SLICE_COLORS;
    const swatch = palette[idx] || colors.primary;

    el.innerHTML = `
      <div style="display:flex;align-items:center;gap:6px;font-weight:700;">
        <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${swatch};"></span>
        <span>${label}</span>
      </div>
      <div style="margin-top:4px;color:${colors.textSecondary};font-weight:600;">
        ${value} (${pct}%)
      </div>
    `;
  }

  const { offsetLeft, offsetTop } = chart.canvas;
  const rect = chart.canvas.getBoundingClientRect();
  const scrollX = window.scrollX || window.pageXOffset;
  const scrollY = window.scrollY || window.pageYOffset;

  el.style.opacity = 1;
  el.style.left = rect.left + scrollX + tooltip.caretX + 14 + "px";
  el.style.top = rect.top + scrollY + tooltip.caretY - 18 + "px";

  // keep offsetLeft/offsetTop referenced so it isn't tree-shaken
  void offsetLeft; void offsetTop;
};

/*
 * The figure in the hole. In points mode the menu's energy is printed as points (kcal ÷ 100) under
 * "נקודות". The slices are whatever the caller passed and are not converted here — a caller in
 * points mode passes them in points, so the tooltip reads in the same unit as the centre.
 */
const centerText = (calories, total, mode) => {
  if (!calories) return { title: `${total}`, subtitle: "סה״כ" };
  if (mode === POINTS) return { title: energyFigure(calories, mode).text, subtitle: energyLabel(mode) };
  return { title: `${calories}`, subtitle: "קלוריות" };
};

const NutritionDonut = ({ carbs = 0, proteins = 0, fats = 0, calories = 0, size = 200, sliceColors = DEFAULT_SLICE_COLORS, emptyBorderColor = colors.primaryLight }) => {
  const chartRef = useRef(null);
  const mode = useNutritionDisplayMode();

  useEffect(() => {
    return () => {
      const el = document.getElementById(TOOLTIP_ID);
      if (el && !document.querySelector("canvas[data-nutrition-donut]")) {
        el.style.opacity = 0;
      }
    };
  }, []);

  const { data, options, hasData } = useMemo(() => {
    const values = [carbs, proteins, fats];
    const total = values.reduce((a, b) => a + b, 0);
    return {
      hasData: total > 0,
      data: {
        labels: LABELS,
        datasets: [
          {
            data: values,
            backgroundColor: sliceColors,
            borderColor: "#FCFCFD",
            borderWidth: 2,
            hoverOffset: 12,
            hoverBorderWidth: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        cutout: "62%",
        animation: { animateRotate: true, animateScale: true, duration: 900 },
        layout: { padding: 12 },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: externalTooltip,
          },
          centerText: total > 0
            ? centerText(calories, total, mode)
            : { title: "" },
        },
      },
    };
  }, [carbs, proteins, fats, calories, sliceColors, mode]);

  if (!hasData) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: "50%",
          border: `2px dashed ${emptyBorderColor}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: colors.textSecondary,
          fontSize: 12,
        }}
      >
        אין נתונים
      </Box>
    );
  }

  return (
    <Box sx={{ width: size, height: size, position: "relative" }}>
      <Doughnut
        ref={chartRef}
        data={data}
        options={options}
        plugins={[centerTextPlugin]}
        data-nutrition-donut
      />
    </Box>
  );
};

export default NutritionDonut;
